<template>
  <div id="map-search" class="map-search">
    <search-bar
      class="desktop-search-bar"
      :text-input="queryInput"
      :placeholder="searchPlaceholder"
      role="combobox"
      aria-owns="search-content-card"
      aria-haspopup="listbox"
      :aria-expanded="searchCurrentlyActive"
      data-test="search-bar"
      @submit-search-clicked="handleSearchSubmitClicked"
      @input-keyup="handleSearchKeyup"
      @search-focus="handleSearchFocus"
      @reset-search="handleSearchBarReset"
    />
    <div
      v-show="searchCurrentlyActive"
      id="search-content-card"
      class="content-card"
    >
      <x-segment-control
        v-if="privateLandToggle"
        class="search-mode-control"
        :segments="searchModes"
        appearance-size="full-width"
        @change="handleSearchModeChange"
      />
      <div
        v-if="hasAutocompleteResults || hasSearchContent || noSearchResults"
        class="results-content"
      >
        <div v-if="hasAutocompleteResults" class="autocomplete-results">
          <slot name="autocomplete-results">
            <autocomplete-results-list
              v-if="hasAutocompleteResults"
              :results="autocompleteResults"
              @result-selected="handleAutocompleteResultSelected"
            />
          </slot>
        </div>

        <div v-else-if="hasSearchContent" class="search-results-content">
          <div
            v-if="resultCategoriesActive"
            class="search-results-chip-menu"
            data-test="search-results-chip-menu"
          >
            <x-chip-menu
              :chips="chips"
              :all-chip="true"
              :scroll="true"
              @click="handleSearchGroupClick"
            />
          </div>

          <slot name="search-results">
            <x-loading-spinner
              v-if="loading"
              class="loading-container"
              data-test="search-loading"
            />

            <ys-text
              v-if="hasSearchResults && !loading"
              type="body1"
              class="results-count"
              data-test="search-results-label"
            >
              {{ totalResultsLabel }}
            </ys-text>

            <results-list
              v-if="hasSearchResults"
              :search-results="searchResults"
              :show-all="true"
              data-test="search-results-list"
              @result-selected="handleResultSelected"
            />
          </slot>
        </div>
        <div
          v-else-if="noSearchResults"
          class="no-results-container"
          data-test="no-results"
        >
          <ys-text type="body1" class="no-results-message">
            No Results Found
          </ys-text>
          <slot name="noResults" />
        </div>
      </div>
      <div v-else class="empty-content">
        <slot name="emptyStateTop" />
        <slot name="emptyState">
          <!-- Examples -->
          <search-examples
            v-if="isExamplesVisible"
            data-test="search-examples"
            @search-example-selected="handleExampleSearchClick"
          />

          <!-- History -->
          <search-history
            v-if="isHistoryVisible"
            data-test="search-history"
            @search-history-selected="handleHistoryItemClick"
          />
        </slot>
        <slot name="emptyStateBottom" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSearch, type UseSearchConfig } from '@onxmaps/search-web-vue'

import '@onxmaps/yellowstone-ui/ys-text'
import searchBar from '@/components/search/search-bar.vue'
import xSegmentControl from '@/components/shared/x-segment-control.vue'
import xChipMenu from '@/components/shared/x-chip-menu/index.vue'
import xLoadingSpinner from '@/components/shared/x-loading-spinner/index.vue'
import autocompleteResultsList from '@/components/search/autocomplete-results-list.vue'
import resultsList from '@/components/search/results-list.vue'
import searchExamples from '@/components/search/search-examples.vue'
import searchHistory from '@/components/search/search-history.vue'

import { getApolloClient } from '@/lib/apollo-client'
import { createHttpClient } from '@/lib/http-client'
import pubsub from '@/lib/pubsub'
import { getCountWithLabel } from '@/lib/display-formatting'
import {
  searchIsActive,
  setSearchIsActive,
  searchMode,
  setSearchMode,
  setGlyphIconMap,
  addQueryToHistory,
  searchHistory as history,
  setShowSearchArea,
  activeLastSearchTerm,
  setLastQueryInputFetchedLocationSearch,
  setLastQueryInputFetchedLandownerSearch,
  setSearchOrigin,
  handleAutocompleteSelection,
  handleSearchResultSelection,
} from '@/services/search-service'
import { SearchMode } from '@/services/search-service/constants'
import type { SearchResultProps } from '@/services/search-service/types'
import { Vertical } from '@/types/verticals'
import { useSearchSpatialFilter } from '@/composables/search/use-search-spatial-filter'
import { useSearchPrivateLandFilter } from '@/composables/search/use-search-private-land-filter'
import { computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import type { AutocompleteResult } from '@onxmaps/search-web'
import { SEARCH_ORIGINS } from '@/lib/enums-marketing'

type SearchTemplateProps = {
  /** Main configuration for the search experience. Docs: https://search-web.docs.daily.onxmaps.com/?path=/docs/composables-usesearch--docs#config-usesearchconfig-optional  */
  searchConfig?: UseSearchConfig
  vertical: Vertical
  placeholder?: string
  privateLandToggle?: boolean
  searchMapMarkers?: boolean
  searchCategoryChips?: boolean
}

const {
  searchConfig = {},
  vertical,
  placeholder = 'Search',
  privateLandToggle = true,
  searchMapMarkers = false,
  searchCategoryChips = true,
} = defineProps<SearchTemplateProps>()

const apolloClient = getApolloClient()
const httpClient = createHttpClient()

const router = useRouter()
const route = useRoute()

// Augment search config with spatial data (bounds + autocomplete location) from the map viewer
const spatialSearchConfig = useSearchSpatialFilter(searchConfig)
// Final config that integrates private land filter when necessary
const finalSearchConfig = privateLandToggle
  ? useSearchPrivateLandFilter(spatialSearchConfig)
  : spatialSearchConfig

// Core search logic
const {
  queryInput,
  requestedQuery,
  loading,
  error,
  autocompleteLoading,
  autocompleteRequestedQuery,
  autocompleteResults,
  searchResults,
  groupedResults,
  currentResultsGroup,
  handleInputKeyup,
  executeSearch,
  clearInput,
  clearResults,
} = useSearch(apolloClient, httpClient, finalSearchConfig)

const MIN_AUTOCOMPLETE_CHARS = 3

const searchCurrentlyActive = computed<boolean>(() => searchIsActive())
const searchPlaceholder = computed<string>(
  () => activeLastSearchTerm.value || placeholder
)
const isLocationMode = computed<boolean>(
  () => searchMode() === SearchMode.Location
)
const isLandownerMode = computed<boolean>(
  () => searchMode() === SearchMode.Landowner
)
const baseMapRoute = computed<string>(() => `${vertical}-map`)

const totalResultsLabel = computed<string>(() =>
  getCountWithLabel(searchResults.value.length, 'result')
)

const resultCategoriesActive = computed<boolean>(
  () => hasSearchResults.value && searchCategoryChips
)

const searchModes = computed(() => {
  return [
    {
      value: SearchMode.Location,
      label: 'Location',
      selected: isLocationMode.value,
    },
    {
      value: SearchMode.Landowner,
      label: 'Private Land',
      selected: isLandownerMode.value,
    },
  ]
})

const chips = computed(() =>
  groupedResults.value.map((group) => {
    return { label: group.name }
  })
)

const hasAutocompleteResults = computed<boolean>(
  () => !!autocompleteResults.value.length
)

const hasSearchContent = computed<boolean>(
  () => !!error.value || loading.value || !!requestedQuery.value
)

const hasRequestedQuery = computed<boolean>(
  () => !!(autocompleteRequestedQuery.value || requestedQuery.value)
)

const hasSearchResults = computed<boolean>(() => !!searchResults.value.length)

const hasMinCharsForResults = computed(
  () => queryInput.value.length > MIN_AUTOCOMPLETE_CHARS
)

const noSearchResults = computed<boolean>(() => {
  return (
    !loading.value &&
    !autocompleteLoading.value &&
    !error.value &&
    hasRequestedQuery.value &&
    !hasSearchResults.value &&
    !hasAutocompleteResults.value &&
    hasMinCharsForResults.value
  )
})

const isExamplesVisible = computed<boolean>(() => {
  if (loading.value || error.value) {
    return false
  }
  if (history.value?.length === 0) {
    return true
  }
  return false
})

const isHistoryVisible = computed<boolean>(() => {
  if (loading.value || error.value) {
    return false
  }
  // If there are no search results and there is a history to display
  if (history.value?.length) {
    return true
  }
  return false
})

const handleSearchKeyup = (event: KeyboardEvent) => {
  // queryInput is set manually, since the SearchBar component doesn't use two-way binding for its text-input
  queryInput.value = (event.target as HTMLInputElement).value
  handleInputKeyup(event)

  if (queryInput.value.length < MIN_AUTOCOMPLETE_CHARS) {
    clearResults()
    autocompleteResults.value = []
    setLastFetchedQuery('')
  }
}

const handleSearchSubmitClicked = () => {
  addQueryToHistory(requestedQuery.value)
  executeSearch()
}

const handleSearchFocus = () => {
  pubsub.publish('searchFocused')

  setSearchIsActive(true)
  queryInput.value = activeLastSearchTerm.value || ''

  router.push({ name: baseMapRoute.value })
}

const handleSearchBarReset = () => {
  setSearchIsActive(false)
  clearInput()
  clearResults()
  autocompleteResults.value = []
  setLastQueryInputFetchedLocationSearch('')
  setLastQueryInputFetchedLandownerSearch('')
}

const handleSearchModeChange = (newMode: SearchMode) => {
  setSearchMode(newMode)
  executeSearch()
}

const handleSearchGroupClick = (category: string) => {
  currentResultsGroup.value = category
}

const handleAutocompleteResultSelected = async (
  result: AutocompleteResult,
  index: number
) => {
  const handleSearchResult = (result: SearchResultProps) => {
    handleResultSelected(result, false, true)
  }
  const handleSuggestionResult = (query: string) => {
    setSearchOrigin(
      index === 0
        ? SEARCH_ORIGINS.AUTOCOMPLETE_CURRENT_QUERY
        : SEARCH_ORIGINS.AUTOCOMPLETE_SUGGESTED_QUERY
    )
    queryInput.value = query
    executeSearch()
  }

  await handleAutocompleteSelection({
    result,
    index,
    onResultSelected: handleSearchResult,
    onSuggestionSelected: handleSuggestionResult,
  })
}

const handleResultSelected = (
  result: SearchResultProps,
  fromSearchMarker = false,
  fromAutocompleteResults = false
) => {
  handleSearchResultSelection({
    result,
    query: requestedQuery.value,
    fromSearchMarker,
    fromAutocompleteResults,
    mapMarkersEnabled: searchMapMarkers,
  })
}

const handleExampleSearchClick = (query: string) => {
  queryInput.value = query
  handleSearchSubmitClicked()
}

const handleHistoryItemClick = (query: string) => {
  // FUTURE - implement recent_search_click event in FOX-11978
  queryInput.value = query
  handleSearchSubmitClicked()
}

/** Helper to set the last fetched query based upon current search mode */
const setLastFetchedQuery = (query: string) => {
  if (isLocationMode.value) {
    setLastQueryInputFetchedLocationSearch(query)
  } else if (isLandownerMode.value) {
    setLastQueryInputFetchedLandownerSearch(query)
  }
}

onMounted(() => {
  // Setting the glyph icon map is only necessary if not using search-web-vue icon components
  setGlyphIconMap()
})

watch(
  () => route.name,
  (to) => {
    // Clear queryInput to ensure any existing search term is shown as the placeholder
    if (to !== baseMapRoute.value) {
      queryInput.value = ''

      setSearchIsActive(false)
    }

    // Remove search-this-area from map anytime route changes
    setShowSearchArea(false)
  }
)

watch(autocompleteResults, (val) => {
  // Update last fetched query when autocomplete results are returned
  if (val.length) {
    setLastFetchedQuery(queryInput.value)
  }
})

watch(searchResults, (val) => {
  // Update last fetched query when search results are returned
  if (val.length) {
    setLastFetchedQuery(requestedQuery.value)
  }
})
</script>

<style lang="scss" scoped>
@use '@/styles/_layer-map.scss';
@use '@/styles/system/_shadow.scss';

.map-search {
  position: relative;
  z-index: layer-map.$z-index-level-controls;
  margin: 0 10px;
  user-select: none;
  pointer-events: all;
  display: inline-block;
  width: var(--card-width);

  .search-bar {
    box-shadow: shadow.$Low;
  }
}

.content-card {
  position: relative;
  top: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 95px);
  overflow-y: auto;
  background-color: var(--ys-color-background-primary);
  margin-left: 0px;
  padding: var(--ys-spacing-250) 0;
  border-radius: var(--ys-radius-150);
  box-shadow: shadow.$Medium;
}

.search-mode-control {
  padding: 0 var(--ys-spacing-250);
}

.loading-container {
  margin-top: var(--ys-spacing-300);
}

.results-count {
  margin-top: var(--ys-spacing-200);
  color: var(--ys-color-text-dim);
  padding-left: var(--ys-spacing-250);
}

.search-results-chip-menu {
  margin-top: var(--ys-spacing-250);
}

.no-results-container {
  padding: 0 var(--ys-spacing-250);
}

.no-results-message {
  color: var(--ys-color-text-dim);
}
</style>
